import BigNumber from 'bignumber.js';
import _ from 'lodash';

import { Eフェーズ種別 } from '@/openapi/enum';
import { Cデータ実態純資産, データListItemResponse } from '@/openapi/user';

import 決算期Utility, { 決算期TableHeader } from '@/classes/common/決算期Utility';
import AbstractデータService, { ServiceType, 帳票データ } from '@/classes/service/AbstractデータService';
import 計画PlbscfService from '@/classes/service/計画/計画PlbscfService';
import 実態BSService from '@/classes/service/分析/実態BSService';

// ------------------------------------------------------------------
// type
// ------------------------------------------------------------------

/**
 * tデータ用
 */
export type 実態純資産ListItem = Cデータ実態純資産 & {
  データpk?: number;
  番号: number;
};
/**
 * parse の返却値
 */
export type 実態純資産 = {
  header: 決算期TableHeader[]; // size：列数
  list: 実態純資産ListItem[]; // size：行数
  // 以下、それぞれ一行の情報
  簿価純資産: Cデータ実態純資産;
  当期純利益: Cデータ実態純資産;
  合計: Cデータ実態純資産;
};

// ------------------------------------------------------------------
// const
// ------------------------------------------------------------------

const CODE_OF_純資産の部_合計 = 'N27';
const CODE_OF_当期純利益 = 'N225';

export default class 実態純資産Service extends AbstractデータService<実態純資産> {
  protected serviceType = ServiceType.実態純資産;

  private 計画PlbscfService: 計画PlbscfService | null = null;

  private 実態BSService: 実態BSService | null = null;

  // ------------------------------------------------------------------
  // getter/setter
  // ------------------------------------------------------------------

  /**
   * 計画PlbscfService
   * @returns
   */
  public get get計画PlbscfService(): 計画PlbscfService | null {
    return this.計画PlbscfService;
  }

  // ------------------------------------------------------------------
  // override
  // ------------------------------------------------------------------

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse(データs: データListItemResponse[], option?: Record<string, AbstractデータService<unknown>>): 実態純資産 {
    // 必要データに絞り込んで計算可能データ化
    const filteredデータs = this.入力分類Filter(データs);

    // 各service 初期化
    // TODO: たくさんこの処理あるからまとめた関数つくる（static?）
    if (this.計画PlbscfService === null) {
      this.計画PlbscfService =
        option?.計画PlbscfService === undefined
          ? new 計画PlbscfService(this.eフェーズ種別)
          : (option?.計画PlbscfService as 計画PlbscfService);
    }
    this.計画PlbscfService.set勘定科目s = this.勘定科目s;
    this.計画PlbscfService.set部門s = this.部門s;

    if (this.実態BSService === null) {
      this.実態BSService =
        option?.実態BSService === undefined
          ? new 実態BSService(Eフェーズ種別.分析フェーズ)
          : (option?.実態BSService as 実態BSService);
    }
    this.実態BSService.set勘定科目s = this.勘定科目s;

    // 計画Plbscf、実態BSの情報を取得
    this.計画PlbscfService.parse(データs);
    const 実態BS = this.実態BSService.parse(データs, { isNoFilter: true });
    const 実態残高B = 実態BS.list.find(
      (listItem) => listItem.value.plbscfYearValue.item.計算Code === CODE_OF_純資産の部_合計
    )!.value.残高比率Group.B.実態残高;

    // DBのデータを設定
    const list: 実態純資産ListItem[] = filteredデータs.map((d) => {
      const cデータ実態純資産 = JSON.parse(d.cデータ) as Cデータ実態純資産;
      return {
        データpk: d.pk,
        番号: d.c番号,
        ...cデータ実態純資産,
      };
    });

    // 画面表示用データを生成
    const v計画PlbscfService = this.計画PlbscfService!;
    const v決算期 = 決算期Utility.get計画決算期(1);
    const 簿価純資産: Cデータ実態純資産 = {
      label: '簿価純資産',
      values: [],
    };
    const 当期純利益: Cデータ実態純資産 = {
      label: '当期純利益',
      values: [],
    };
    const 合計: Cデータ実態純資産 = {
      label: '実態純資産（中小企業特性考慮後）',
      values: [],
    };

    // 年（列）でループ
    v決算期.years.forEach((v決算年, i) => {
      const 簿価純資産Value = v計画PlbscfService.getValueByCodeAnd決算期年月(CODE_OF_純資産の部_合計, v決算年.year);
      const 当期純利益Value = v計画PlbscfService.getValueByCodeAnd決算期年月(CODE_OF_当期純利益, v決算年.year);
      // 合計値を算出
      let 合計Value = 0;
      if (i === 0) {
        // 最初の列（前期）は合計に実態BSの値を設定
        合計Value = 実態残高B;
      } else {
        合計Value = new BigNumber(合計.values[i - 1]).plus(当期純利益Value).toNumber();
        for (const listItem of list) 合計Value = new BigNumber(合計Value).plus(listItem.values[i - 1]).toNumber();
      }

      簿価純資産.values.push(簿価純資産Value);
      当期純利益.values.push(当期純利益Value);
      合計.values.push(合計Value);
    });

    return {
      header: v決算期.header,
      list,
      簿価純資産,
      当期純利益,
      合計,
    };
  }

  /**
   * @override
   * @param データs
   * @param option
   * @returns
   */
  public parse帳票ListItemRequest(
    データs: データListItemResponse[],
    option?: Record<string, AbstractデータService<unknown>>
  ): 帳票データ {
    const v実態純資産 = this.parse(データs, option);
    // テンプレート行設定
    const templateConfig = {
      header: 0,
      row: {
        body: 1,
        copied: 2,
        total: 3,
      },
    };

    // 以下帳票データ整形
    // 初行設定込みで初期化
    const templateRows: number[] = [templateConfig.header];
    const values: unknown[][] = [[null, ...v実態純資産.header.map((h) => h.label)]];

    // 一行追加する処理
    const addRow = (config: number, cデータ実態純資産: Cデータ実態純資産, isBody = false): void => {
      templateRows.push(config);
      const valuesArray: (string | number)[] = cデータ実態純資産.values.map((val) =>
        this.convertByMoneyUnit(val as number)
      );
      if (isBody) valuesArray.unshift('');
      values.push([cデータ実態純資産.label, ...valuesArray]);
    };

    // 簿価純資産
    addRow(templateConfig.row.copied, v実態純資産.簿価純資産);
    // データ行
    v実態純資産.list.forEach((listItem) => {
      addRow(templateConfig.row.body, listItem, true);
    });
    // 当期純利益
    addRow(templateConfig.row.copied, v実態純資産.当期純利益);
    // 合計
    addRow(templateConfig.row.total, v実態純資産.合計);

    return {
      templateRows,
      values,
    };
  }
}
